export interface FloatingMainCommandSize {
  readonly width: number;
  readonly height: number;
}

export interface FloatingMainCommandViewport {
  readonly width: number;
  readonly height: number;
}

export interface FloatingMainCommandStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export const FLOATING_MAIN_COMMAND_DEFAULT_SIZE: FloatingMainCommandSize = {
  width: 576,
  height: 640,
};

export const FLOATING_MAIN_COMMAND_MIN_SIZE: FloatingMainCommandSize = {
  width: 352,
  height: 384,
};

export const FLOATING_MAIN_COMMAND_SIZE_STORAGE_KEY = 'brunel:floating-main-command:size:v1';

const VIEWPORT_HORIZONTAL_GUTTER = 16;
const VIEWPORT_BOTTOM_RESERVE = 88;

function finiteDimension(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}

export function clampFloatingMainCommandSize(
  size: FloatingMainCommandSize,
  viewport: FloatingMainCommandViewport,
): FloatingMainCommandSize {
  const maxWidth = Math.max(280, Math.floor(viewport.width - VIEWPORT_HORIZONTAL_GUTTER));
  const maxHeight = Math.max(300, Math.floor(viewport.height - VIEWPORT_BOTTOM_RESERVE));
  const minWidth = Math.min(FLOATING_MAIN_COMMAND_MIN_SIZE.width, maxWidth);
  const minHeight = Math.min(FLOATING_MAIN_COMMAND_MIN_SIZE.height, maxHeight);

  return {
    width: Math.min(maxWidth, Math.max(minWidth, Math.round(size.width))),
    height: Math.min(maxHeight, Math.max(minHeight, Math.round(size.height))),
  };
}

export function parseFloatingMainCommandSize(value: string | null): FloatingMainCommandSize | null {
  if (value === null) return null;
  try {
    const parsed = JSON.parse(value) as { width?: unknown; height?: unknown };
    const width = finiteDimension(parsed.width);
    const height = finiteDimension(parsed.height);
    return width === null || height === null ? null : { width, height };
  } catch {
    return null;
  }
}

export function loadFloatingMainCommandSizeFromStorage(
  storage: FloatingMainCommandStorage,
  viewport: FloatingMainCommandViewport,
): FloatingMainCommandSize {
  try {
    const stored = parseFloatingMainCommandSize(
      storage.getItem(FLOATING_MAIN_COMMAND_SIZE_STORAGE_KEY),
    );
    return clampFloatingMainCommandSize(
      stored ?? FLOATING_MAIN_COMMAND_DEFAULT_SIZE,
      viewport,
    );
  } catch {
    return clampFloatingMainCommandSize(FLOATING_MAIN_COMMAND_DEFAULT_SIZE, viewport);
  }
}

export function persistFloatingMainCommandSizeToStorage(
  storage: FloatingMainCommandStorage,
  size: FloatingMainCommandSize,
): void {
  storage.setItem(FLOATING_MAIN_COMMAND_SIZE_STORAGE_KEY, JSON.stringify(size));
}

export function resetFloatingMainCommandSizeInStorage(
  storage: FloatingMainCommandStorage,
  viewport: FloatingMainCommandViewport,
): FloatingMainCommandSize {
  try {
    storage.removeItem(FLOATING_MAIN_COMMAND_SIZE_STORAGE_KEY);
  } catch {
    // Reset still applies in-memory when browser storage is unavailable.
  }
  return clampFloatingMainCommandSize(FLOATING_MAIN_COMMAND_DEFAULT_SIZE, viewport);
}

export function browserFloatingMainCommandViewport(): FloatingMainCommandViewport | null {
  if (typeof window === 'undefined') return null;
  return { width: window.innerWidth, height: window.innerHeight };
}

export function loadFloatingMainCommandSize(
  viewport: FloatingMainCommandViewport,
): FloatingMainCommandSize {
  if (typeof window === 'undefined') {
    return clampFloatingMainCommandSize(FLOATING_MAIN_COMMAND_DEFAULT_SIZE, viewport);
  }
  return loadFloatingMainCommandSizeFromStorage(window.localStorage, viewport);
}

export function persistFloatingMainCommandSize(size: FloatingMainCommandSize): void {
  if (typeof window === 'undefined') return;
  try {
    persistFloatingMainCommandSizeToStorage(window.localStorage, size);
  } catch {
    // Browser storage is best-effort; resizing still works for the current session.
  }
}

export function resetFloatingMainCommandSize(viewport: FloatingMainCommandViewport): FloatingMainCommandSize {
  if (typeof window === 'undefined') {
    return clampFloatingMainCommandSize(FLOATING_MAIN_COMMAND_DEFAULT_SIZE, viewport);
  }
  return resetFloatingMainCommandSizeInStorage(window.localStorage, viewport);
}
